/**
 * Standardized Confirmation Modal
 *
 * A reusable modal component for confirmation dialogs with consistent styling.
 * Used for destructive actions, unsaved changes warnings, etc.
 *
 * Usage:
 * const { showConfirm } = useConfirmModal();
 *
 * showConfirm({
 *   title: 'Unsaved Changes',
 *   content: 'You have unsaved changes. Are you sure you want to leave?',
 *   onOk: () => navigate(-1),
 * });
 */

import { theme, App } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { controlHeightTokens } from '@/styles/tokens';

export interface ConfirmModalOptions {
  /** Modal title */
  title: string;

  /** Modal content/message */
  content: string;

  /** Callback when OK button is clicked */
  onOk: () => void;

  /** Callback when Cancel button is clicked (optional) */
  onCancel?: () => void;

  /** OK button text (default: 'Leave') */
  okText?: string;

  /** Cancel button text (default: 'Stay') */
  cancelText?: string;

  /** Use danger styling for OK button (default: true) */
  danger?: boolean;
}

/**
 * Hook to access standardized confirm modal
 */
export const useConfirmModal = () => {
  const { modal } = App.useApp();
  const { token } = theme.useToken();
  const { t } = useTranslation('common');

  const showConfirm = ({
    title,
    content,
    onOk,
    onCancel,
    okText = t('modals.leave'),
    cancelText = t('modals.stay'),
    danger = true,
  }: ConfirmModalOptions) => {
    modal.confirm({
      title,
      icon: <ExclamationCircleOutlined style={{ color: token.colorWarning }} />,
      content,
      okText,
      okType: danger ? 'danger' : 'primary',
      cancelText,
      okButtonProps: {
        style: { height: `${controlHeightTokens.controlHeight}px` },
      },
      cancelButtonProps: {
        style: { height: `${controlHeightTokens.controlHeight}px` },
      },
      onOk,
      onCancel,
    });
  };

  return { showConfirm };
};

/**
 * Specialized hook for unsaved changes confirmation
 */
export const useUnsavedChangesModal = () => {
  const { showConfirm } = useConfirmModal();
  const { t } = useTranslation('common');

  const confirmUnsavedChanges = (onConfirm: () => void) => {
    showConfirm({
      title: t('modals.unsavedChanges'),
      content: t('modals.unsavedChangesContent'),
      onOk: onConfirm,
    });
  };

  return { confirmUnsavedChanges };
};
